import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Modal, ModalBody } from "reactstrap";
import { disconnect } from "../../../actions/admin/calendar-integration-actions";
import { sendPostRequest } from "../../../actions/app-actions";
import { notification } from "../../../utils/utility";
import { getStaffBusinessId } from "../../../utils/authHelper";
import { startCalendarOAuth } from "../../../utils/calendarOAuth";
import isEmpty from "../../../validation/is-empty";
import Loader from "../../../components/Loader/Loader";
import PageHeader from "../../../components/PageHeader/PageHeader";
import DisconnectModal from "../../admin/calendar-integrations/disconnect-modal";
import CheckForConflictModal from "../../admin/calendar-integrations/check-for-conflict-modal";
import AddToCalendarModal from "../../admin/calendar-integrations/add-to-calendar-modal";
import OutlookCheckForConflictModal from "../../admin/calendar-integrations/outlook/check-for-conflict-modal";
import { GOOGLE_CALENDAR_ID, OUTLOOK_CALENDAR_ID } from "../../../data/integration-app";
import OutlookLogoSrc from "../../../assets/icons/ms-outlook.svg";
import GoogleCalendarImgSrc from "../../../assets/calendar-integration/images/google_calendar.svg";

/* ── Real Google Calendar SVG logo ──────────────────────── */
const GoogleCalendarLogo = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="46" height="46" rx="5" fill="white" stroke="#dadce0" strokeWidth="1.5" />
    <path d="M1 6C1 3.24 3.24 1 6 1H42C44.76 1 47 3.24 47 6V17H1V6Z" fill="#1A73E8" />
    <text x="24" y="13.5" textAnchor="middle" fill="white" fontSize="9" fontWeight="700"
      fontFamily="Google Sans,Roboto,Arial,sans-serif">31</text>
    <text x="24" y="37" textAnchor="middle" fill="#1A73E8" fontSize="19" fontWeight="700"
      fontFamily="Google Sans,Roboto,Arial,sans-serif">31</text>
    <circle cx="9" cy="43.5" r="2.5" fill="#EA4335" />
    <circle cx="16" cy="43.5" r="2.5" fill="#FBBC04" />
    <circle cx="23" cy="43.5" r="2.5" fill="#34A853" />
    <circle cx="30" cy="43.5" r="2.5" fill="#4285F4" />
  </svg>
);

/* ── Microsoft Outlook logo ──────────────────────────────── */
const OutlookLogo = ({ size = 40 }) => (
  <img src={OutlookLogoSrc} alt="Microsoft Outlook" width={size} height={size}
    style={{ objectFit: "contain" }} />
);

/* ── Icons ───────────────────────────────────────────────── */
const PlusIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const GridIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    <rect x="9" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    <rect x="1" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    <rect x="9" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
/* Shield with a checkmark — "protect against double-bookings / conflict check" */
const ShieldCheckIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M10 2L3 5.2V10c0 4 2.9 7.4 7 8.5 4.1-1.1 7-4.5 7-8.5V5.2L10 2Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M7 10.5l2.2 2.2L13.5 8"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
/* Calendar with a plus — "add new bookings to this calendar" */
const CalendarPlusIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="2" y="4" width="13" height="12.5" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M2 8.5h13" stroke="currentColor" strokeWidth="1.6" />
    <path d="M6 2.5v2.5M11 2.5v2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M8.5 11.5v4M6.5 13.5h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const ArrowRightIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ─────────────────────────────────────────────────────────── */

const ConnectedCalendars = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dataSet, setDataSet] = useState([]);
  const [calendarData, setCalendarData] = useState(null);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showOutlookConflictModal, setShowOutlookConflictModal] = useState(false);
  const [showAddToCalendarModal, setShowAddToCalendarModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [integrationId, setIntegrationId] = useState("");
  const [reconnecting, setReconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const allowance = Number(props.allowance || 1);

  useEffect(() => {
    setDataSet(props.data || []);
  }, [props.data]);

  /* ── Helpers ─────────────────────────────────────────── */
  const getProviderLogo = (appId, size = 40) => {
    if (parseInt(appId) === GOOGLE_CALENDAR_ID) return <GoogleCalendarLogo size={size} />;
    return <OutlookLogo size={size} />;
  };

  const getProviderName = (appId) => {
    if (parseInt(appId) === GOOGLE_CALENDAR_ID) return "Google Calendar";
    if (parseInt(appId) === OUTLOOK_CALENDAR_ID) return "Microsoft Outlook";
    return "Calendar";
  };

  const getEmail = (data) => {
    try { return JSON.parse(data?.meta_data)?.email || ""; } catch { return ""; }
  };

  /* ── Connect OAuth (staff) ───────────────────────────── */
  const onConnect = async (provider) => {
    if (connecting) return;
    setConnecting(true);
    setShowConnectModal(false);
    try {
      await startCalendarOAuth({
        businessId: getStaffBusinessId(),
        provider,
        owner: "staff",
      });
    } finally {
      setConnecting(false);
    }
  };

  /* ── Reconnect (staff) ───────────────────────────────── */
  const onReconnect = async (e, appId) => {
    e.preventDefault();
    if (reconnecting) return;
    setReconnecting(true);
    try {
      await startCalendarOAuth({
        businessId: getStaffBusinessId(),
        provider: parseInt(appId) === GOOGLE_CALENDAR_ID ? "google" : "outlook",
        owner: "staff",
      });
    } finally {
      setReconnecting(false);
    }
  };

  /* ── Disconnect ──────────────────────────────────────── */
  const disconnect = (id) => {
    setIntegrationId(id);
    setShowDisconnectModal(true);
  };

  const disconnectFromCalendar = (id) => {
    const { disconnect: disconnectAction, setIntegrationDataSet, setCalendarIntegrationView } = props;
    setIsLoading(true);
    disconnectAction({
      data: { id },
      onSuccess: function () {
        const updated = dataSet.filter((obj) => obj.id !== id);
        setDataSet(updated);
        if (setIntegrationDataSet) setIntegrationDataSet(updated);
        if (updated.length === 0 && setCalendarIntegrationView) {
          setCalendarIntegrationView("2");
        }
        setIsLoading(false);
      },
      onError: function () {
        setIsLoading(false);
      },
    });
  };

  /* ── Check for conflict ──────────────────────────────── */
  const onCheckForConflict = (data) => {
    setCalendarData(data);
    setIntegrationId(data?.id);
    if (parseInt(data.app_id) === GOOGLE_CALENDAR_ID) {
      setShowConflictModal(true);
    } else {
      setShowOutlookConflictModal(true);
    }
  };

  const onUpdateConflictCals = (selectedCals) => {
    const { sendPostRequest } = props;
    setIsLoading(true);
    sendPostRequest({
      url: `/admin-selected-calendars`,
      data: { integrationId, selectedCals },
      onSuccess: function () {
        const updated = [...dataSet];
        const idx = updated.findIndex((el) => el.id === integrationId);
        if (idx !== -1) updated[idx].selected_calendars = JSON.stringify(selectedCals);
        setDataSet(updated);
        setShowConflictModal(false);
        setShowOutlookConflictModal(false);
        setIsLoading(false);
        notification.success("Calendar conflict settings updated.");
      },
      onError: function () {
        setIsLoading(false);
      },
    });
  };

  /* ── Add to calendar ─────────────────────────────────── */
  const onAddToCalendar = (data) => {
    setCalendarData(data);
    setIntegrationId(data?.id);
    setShowAddToCalendarModal(true);
  };

  const onUpdateAddToCalendar = (data) => {
    const { sendPostRequest } = props;
    setIsLoading(true);
    sendPostRequest({
      url: `/add-to-calendar`,
      data,
      onSuccess: function (resData) {
        const updated = [...dataSet];
        const targetIdx = updated.findIndex((el) => el.id === data.app_integration_id);
        const prevIdx = updated.findIndex((el) => el.id === integrationId);
        // Preserve the old config id before we null it (needed when same account re-saved)
        const savedConfigId = updated[prevIdx]?.config?.id;
        if (prevIdx !== -1) updated[prevIdx].config = null;
        if (targetIdx !== -1) {
          // Use backend-returned id for new configs; fall back to preserved id if same account
          const configId = resData?.id || (targetIdx === prevIdx ? savedConfigId : null);
          updated[targetIdx].config = {
            id: configId,
            add_new_event_status: data.add_new_event_status,
            selected_calendar_id: data.selected_calendar_id,
            delete_event_status: data.delete_event_status,
          };
        }
        setDataSet(updated);
        setShowAddToCalendarModal(false);
        setIsLoading(false);
        notification.success("Booking calendar updated.");
      },
      onError: function () {
        setIsLoading(false);
      },
    });
  };

  /* ── Derived state ───────────────────────────────────── */
  const isReady =
    dataSet.length > 0 &&
    dataSet.some((d) => !isEmpty(d.config) && parseInt(d.needs_reconnect) !== 1);

  const conflictCount = dataSet.reduce((acc, d) => {
    try {
      const cals = JSON.parse(d.selected_calendars) || [];
      return acc + cals.length;
    } catch { return acc; }
  }, 0);

  const addToCalEntry = dataSet.find((d) => !isEmpty(d.config));
  const addToCalEmail = addToCalEntry ? getEmail(addToCalEntry) : null;

  const maxDisplay = allowance;
  const atLimit = dataSet.length >= allowance;

  /* ── Render ──────────────────────────────────────────── */
  return (
    <>
      <Loader isShowLoader={isLoading || connecting} />

      {/* Modals */}
      {showDisconnectModal && (
        <DisconnectModal
          recordId={integrationId}
          showDisconnectModal={showDisconnectModal}
          hideDisconnectModal={() => { setIntegrationId(""); setShowDisconnectModal(false); }}
          disconnect={disconnectFromCalendar}
        />
      )}
      {showConflictModal && (
        <CheckForConflictModal
          data={calendarData}
          showConflictModal={showConflictModal}
          hideConflictModal={() => { setIntegrationId(""); setShowConflictModal(false); }}
          setIsLoading={setIsLoading}
          onUpdateCheckForConflictCals={onUpdateConflictCals}
        />
      )}
      {showOutlookConflictModal && (
        <OutlookCheckForConflictModal
          data={calendarData}
          showConflictModal={showOutlookConflictModal}
          hideConflictModal={() => { setIntegrationId(""); setShowOutlookConflictModal(false); }}
          setIsLoading={setIsLoading}
          onUpdateCheckForConflictCals={onUpdateConflictCals}
        />
      )}
      {showAddToCalendarModal && (
        <AddToCalendarModal
          showAddToCalendarModal={showAddToCalendarModal}
          hideAddToCalendarModal={() => { setIntegrationId(""); setShowAddToCalendarModal(false); }}
          setIsLoading={setIsLoading}
          data={calendarData}
          onUpdateAddToCalendar={onUpdateAddToCalendar}
          dataSet={dataSet}
          getConnectionIcon={(appId) =>
            parseInt(appId) === GOOGLE_CALENDAR_ID
              ? GoogleCalendarImgSrc
              : OutlookLogoSrc
          }
        />
      )}

      {/* ── Connect calendar modal ─────────────────────── */}
      <Modal
        isOpen={showConnectModal}
        toggle={() => setShowConnectModal(false)}
        centered
        className="ci-connect-modal"
      >
        <ModalBody>
          <div className="ci-modal-header">
            <h2>Connect a calendar</h2>
            <button onClick={() => setShowConnectModal(false)} aria-label="Close">×</button>
          </div>
          <p className="ci-modal-sub">
            Choose the account you want to connect. You'll be asked to authorize
            access with the provider.
          </p>

          <button
            className="ci-provider-option"
            onClick={() => onConnect("google")}
            disabled={connecting || atLimit}
          >
            <div className="ci-provider-logo"><GoogleCalendarLogo size={40} /></div>
            <div className="ci-provider-info">
              <strong>Google Calendar</strong>
              <span>Connect a Google account</span>
            </div>
            <div className="ci-provider-arrow"><ArrowRightIcon /></div>
          </button>

          <button
            className="ci-provider-option"
            onClick={() => onConnect("outlook")}
            disabled={connecting || atLimit}
          >
            <div className="ci-provider-logo"><OutlookLogo size={40} /></div>
            <div className="ci-provider-info">
              <strong>Microsoft Outlook</strong>
              <span>Connect a Microsoft 365 account</span>
            </div>
            <div className="ci-provider-arrow"><ArrowRightIcon /></div>
          </button>

          {atLimit && (
            <p style={{ marginTop: 12, fontSize: 13, color: "#b54708", textAlign: "center" }}>
              You have reached your calendar limit ({dataSet.length}/{maxDisplay}).
            </p>
          )}
        </ModalBody>
      </Modal>

      {/* ── Main page ─────────────────────────────────── */}
      <div className="ci-page">

        {/* Header */}
        <PageHeader
          title="Calendar connections"
          subtitle="Connect your calendars to keep your availability accurate and add new bookings automatically."
          actions={
            <button
              className="ci-connect-btn"
              onClick={() => {
                if (atLimit) {
                  notification.error(`You have reached your calendar limit (${dataSet.length}/${maxDisplay}).`);
                  return;
                }
                setShowConnectModal(true);
              }}
              disabled={connecting}
            >
              <PlusIcon />
              Connect calendar
            </button>
          }
        />

        {/* Status bar */}
        <div className="ci-status-bar">
          <div className="ci-status-tile">
            <div className="ci-status-tile__icon"><GridIcon /></div>
            <div>
              <strong>{dataSet.length} of {maxDisplay} accounts connected</strong>
              <span>Add calendars from Google or Microsoft</span>
            </div>
          </div>
          <div className="ci-status-tile">
            <div className="ci-status-tile__icon" style={{ color: isReady ? "#067647" : "#667085" }}>
              <CheckCircleIcon />
            </div>
            <div>
              <strong>{isReady ? "Ready to accept bookings" : "Setup required"}</strong>
              <span>
                {isReady
                  ? "Your availability and booking destination are set"
                  : "Connect a calendar and configure your preferences"}
              </span>
            </div>
          </div>
        </div>

        {/* Connected accounts */}
        <div className="ci-card">
          <div className="ci-card__head">
            <div>
              <h2>Connected accounts</h2>
              <p>Manage the accounts MeetOcto can access.</p>
            </div>
            {dataSet.length > 0 && (
              <span className="ci-card__counter">{dataSet.length} / {maxDisplay}</span>
            )}
          </div>

          {dataSet.length === 0 ? (
            <div className="ci-empty">
              <strong>No calendars connected</strong>
              <span>Connect Google Calendar or Microsoft Outlook to get started.</span>
              <button
                className="ci-empty-connect-btn"
                onClick={() => setShowConnectModal(true)}
              >
                <PlusIcon />
                Connect calendar
              </button>
            </div>
          ) : (
            <div className="ci-account-list">
              {dataSet.map((data) => {
                const needsReconnect = parseInt(data.needs_reconnect) === 1;
                const email = getEmail(data);
                return (
                  <div
                    key={data.id}
                    className={`ci-account-row${needsReconnect ? " is-expired" : ""}`}
                  >
                    <div className="ci-account-logo">
                      {getProviderLogo(data.app_id, 40)}
                    </div>
                    <div className="ci-account-info">
                      <strong>{getProviderName(data.app_id)}</strong>
                      <span>{email}</span>
                    </div>
                    <div className="ci-account-actions">
                      {needsReconnect ? (
                        <>
                          <span className="ci-badge-expired">⚠ Expired</span>
                          <button
                            className="ci-btn-reconnect"
                            onClick={(e) => onReconnect(e, data.app_id)}
                            disabled={reconnecting}
                          >
                            Reconnect
                          </button>
                        </>
                      ) : (
                        <span className="ci-badge-connected">Connected</span>
                      )}
                      <button
                        className="ci-btn-disconnect"
                        onClick={() => disconnect(data.id)}
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Calendar preferences — only when at least one calendar connected */}
        {dataSet.length > 0 && (
          <div className="ci-card">
            <div className="ci-card__head">
              <div>
                <h2>Calendar preferences</h2>
                <p>Choose which calendars affect availability and where bookings appear.</p>
              </div>
            </div>

            <div className="ci-prefs-grid">
              {/* Check for conflicts — one row per connected account */}
              <div className="ci-pref-cell">
                <div className="ci-pref-icon"><ShieldCheckIcon /></div>
                <h3>Check for conflicts</h3>
                <p>Busy events on these calendars will block booking times.</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                  {dataSet.map((connection) => {
                    const calCount = (() => {
                      try { return (JSON.parse(connection.selected_calendars) || []).length; } catch { return 0; }
                    })();
                    return (
                      <div key={connection.id} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: 8, padding: "8px 10px",
                        background: "#F9FAFB", borderRadius: 8,
                        border: "1px solid #E5E7EB",
                      }}>
                        <span style={{ fontSize: 13, color: "#374151", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {getEmail(connection)}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                          <span style={{ fontSize: 12, color: "#6B7280" }}>
                            {calCount > 0 ? `${calCount} selected` : "None"}
                          </span>
                          <button
                            className="ci-pref-link"
                            style={{ margin: 0 }}
                            onClick={() => onCheckForConflict(connection)}
                          >
                            Edit →
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Add bookings to */}
              <div className="ci-pref-cell">
                <div className="ci-pref-icon"><CalendarPlusIcon /></div>
                <h3>Add bookings to</h3>
                <p>New bookings will be added to this calendar.</p>
                <div className="ci-pref-footer">
                  <span className="ci-pref-value">
                    {addToCalEmail || "Not configured"}
                  </span>
                  {dataSet.length > 0 && (
                    <button
                      className="ci-pref-link"
                      onClick={() =>
                        onAddToCalendar(
                          dataSet.find((d) => !isEmpty(d.config)) || dataSet[0]
                        )
                      }
                    >
                      {addToCalEmail ? "Change calendar →" : "Set up →"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default connect(null, {
  disconnect,
  sendPostRequest,
})(ConnectedCalendars);
